import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, MapPin, Search, Asterisk } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { CommunityBar } from "@/components/CommunityBar";
import { ScrollTopButton } from "@/components/ScrollTopButton";
import { MegaMenuPanel } from "@/components/MegaMenuPanel";
import { FooterNewsletter } from "@/components/FooterNewsletter";
import { useCookieConsent } from "@/components/CookieConsent";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ca17e8cf-048e-4a40-9d83-d545b8623e13/artifacts/uzgvzj2q_logo-1-768x339.png";

const NAV = [
  { to: "/", label: "Inicio", testid: "nav-link-inicio", end: true, mega: null },
  { to: "/noticias", label: "Noticias", testid: "nav-link-historias", mega: "news" },
  { to: "/canciones", label: "Canciones", testid: "nav-link-canciones", mega: "songs" },
  { to: "/agenda", label: "Agenda", testid: "nav-link-agenda", badge: "Madrid", mega: "agenda" },
  { to: "/playlists", label: "Playlist", testid: "nav-link-playlists", mega: "playlists" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const cookies = useCookieConsent();

  useEffect(() => { setOpen(false); setSearchOpen(false); }, [location.pathname]);

  const [mega, setMega] = useState(null);
  useEffect(() => { setMega(null); }, [location.pathname]);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="relative min-h-screen" data-testid="app-shell">
      {/* Top bar */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl transition-all duration-300"
        onMouseLeave={() => setMega(null)}
        style={{
          background: scrolled ? "#FFFFFF" : "rgba(255,255,255,0.85)",
          boxShadow: scrolled
            ? "0 6px 22px -8px rgba(42,0,217,0.30), 0 10px 30px -16px rgba(180,216,68,0.35)"
            : "none",
        }}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1400px] flex items-center justify-between px-5 md:px-10 py-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link" onMouseEnter={() => setMega(null)}>
            <img src={LOGO_URL} alt="La Estrofa" width={768} height={339} decoding="async" className="logo-img group-hover:scale-105" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                onMouseEnter={() => setMega(n.mega)}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium flex items-center gap-2 ${isActive ? "text-[#2A00D9]" : "text-[#1A1A1A]"}`
                }
              >
                {({ isActive }) => (
                  <>
                    <span data-active={isActive}>{n.label}</span>
                    {n.badge && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-[#B4D844] text-[#1A1A1A]" data-testid="nav-madrid-badge">
                        {n.badge === "Madrid" && <MapPin size={9} />} {n.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1" onMouseEnter={() => setMega(null)}>
            <button
              className={`inline-flex items-center justify-center h-10 w-10 rounded-full transition-all duration-300 ${searchOpen ? "bg-[#1A1A1A] text-white" : "text-[#2A00D9] border border-black/10 hover:scale-105 shadow-md shadow-black/10"}`}
              style={searchOpen ? undefined : { background: "radial-gradient(circle at 32% 28%, #FFFFFF, #E4E4E4)" }}
              onClick={() => setSearchOpen((s) => !s)}
              aria-label="Buscar"
              data-testid="header-search-toggle"
            >
              {searchOpen ? <X size={18} /> : <Search size={18} />}
            </button>
            <button
              className="md:hidden p-2 rounded-full hover:bg-black/5"
              onClick={() => setOpen(!open)}
              aria-label="Menú"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mega menú (hover, escritorio) */}
        <MegaMenuPanel active={mega} onNavigate={() => setMega(null)} />

        {/* Header search panel */}
        {searchOpen && (
          <div className="border-t border-black/5 bg-[#FAF9F6]" data-testid="header-search-panel">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex justify-center">
              <SearchBar />
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-black/5 bg-[#FAF9F6]" data-testid="mobile-menu">
            <nav className="flex flex-col px-6 py-6 gap-5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`mobile-${n.testid}`}
                  className={({ isActive }) =>
                    `text-lg font-medium flex items-center gap-2 ${isActive ? "text-[#2A00D9]" : "text-[#1A1A1A]"}`
                  }
                >
                  {n.label}
                  {n.badge && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-[#B4D844] text-[#1A1A1A]">
                      {n.badge === "Madrid" && <MapPin size={9} />} {n.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        )}

        {/* Cobalt → lime accent line */}
        <div className="h-[3px] w-full bg-gradient-to-r from-[#2A00D9] via-[#B4D844] to-[#2A00D9]" aria-hidden data-testid="header-accent-line" />
      </header>

      <main className="relative" data-testid="main-content">
        <Outlet />
      </main>

      <footer className="mt-20 bg-[#1A1A1A] text-[#FAF9F6]" data-testid="site-footer">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-16 pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Newsletter */}
            <FooterNewsletter />

            {/* Marca + enlaces */}
            <div className="grid sm:grid-cols-2 gap-10 sm:pt-4">
              <div>
                <img src={LOGO_URL} alt="La Estrofa" width={768} height={339} decoding="async" className="h-9 w-auto mb-5" />
                <p className="text-sm text-white/70 max-w-xs leading-relaxed">
                  Medio digital y canal de comunicación musical: actualidad, lanzamientos y entrevistas a artistas. Desde Madrid, para todas partes.
                </p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#B4D844] mb-4">Secciones</p>
                <ul className="space-y-2.5 text-sm text-white/75">
                  {NAV.map((n) => (
                    <li key={n.to}><Link to={n.to} className="hover:text-[#B4D844] transition-colors">{n.label}</Link></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm text-white/55">
            <p>© {new Date().getFullYear()} La Estrofa. Todos los derechos reservados.</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/quienes-somos" className="hover:text-[#B4D844] transition-colors" data-testid="footer-about-link">Quiénes somos</Link>
              <Link to="/contacto" className="hover:text-[#B4D844] transition-colors" data-testid="footer-contact-link">Contacto</Link>
              <Link to="/politica-de-privacidad" className="hover:text-[#B4D844] transition-colors" data-testid="footer-privacy-link">Política de Privacidad</Link>
              <Link to="/politica-de-cookies" className="hover:text-[#B4D844] transition-colors" data-testid="footer-cookies-link">Política de Cookies</Link>
              <button onClick={() => cookies?.openSettings()} className="hover:text-[#B4D844] transition-colors" data-testid="footer-cookie-settings">Configurar cookies</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Barra de comunidad fija (oculta en el CRM) */}
      {!location.pathname.startsWith("/mazuecos") && <CommunityBar />}

      {/* Flecha volver arriba */}
      <ScrollTopButton />
    </div>
  );
}
